import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import dayjs from 'dayjs';
import { IInsight } from './IInsight';
import { InsightsModel } from './InsightsModel';
import { insightTypeMeta } from './insightTypeMeta';
import { ChevronRightIcon, LockIcon } from '../theme/Icons';
import { Text } from '../components/Text';
import { InsightFeedback } from './InsightFeedback';

const formatDate = (date: string) => dayjs(date).format('MM/DD/YY');

const useStyles = createUseStyles({
    card: {
        background: 'var(--ion-background-color)',
        border: '1px solid var(--ion-color-step-150)',
        borderRadius: 14,
        padding: 14,
        cursor: 'pointer',
    },
    eyebrow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 6,
    },
    // tinted type pill: icon + small-caps label in the type color, on a 10%-opacity wash of it
    type: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.04em',
        borderRadius: 999,
        padding: '3px 9px',
    },
    date: {
        fontSize: 12,
        color: 'var(--ion-color-step-600)',
        flexShrink: 0,
    },
    titleRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 8,
        marginBottom: 6,
    },
    titleText: {
        flex: 1,
        minWidth: 0,
        // match the home insight row + Recent Summaries titles (500), lighter than Text's global
        // subtitle weight (600) without changing the shared Text component
        '& ion-text': {
            fontWeight: 500,
        },
    },
    narrative: {
        marginBottom: 8,
        lineHeight: 1.5,
    },
    meta: {
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: 8,
    },
    lowTag: {
        fontSize: 12,
        color: 'var(--ion-color-step-600)',
        background: 'var(--ion-color-step-100)',
        borderRadius: 999,
        padding: '2px 8px',
    },
    lock: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
    },
});

export interface IInsightListCardProps {
    insight: IInsight;
    model: InsightsModel;
    // Feedback lives on the most recent insights only; older ones are read-only in the list.
    showFeedback: boolean;
    onOpen: () => void;
}

export const InsightListCard: FC<IInsightListCardProps> = ({ insight, model, showFeedback, onOpen }) => {
    const classes = useStyles();

    const { label, Icon, color } = insightTypeMeta(insight.type);

    return (
        <div className={classes.card} data-type="insight-list-card" data-insight-id={insight.insightId} data-id="insight-open" onClick={onOpen}>
            <div className={classes.eyebrow}>
                <span className={classes.type} style={{ color, backgroundColor: `${color}1A` }}>
                    <Icon color="inherit" size="small" />
                    {label}
                </span>
                <span className={classes.date}>{formatDate(insight.dateRangeFrom)} – {formatDate(insight.dateRangeTo)}</span>
            </div>
            <div className={classes.titleRow}>
                <span className={`${classes.titleText} mp-mask`}><Text type="subtitle" color="dark">{insight.title}</Text></span>
                <ChevronRightIcon color="medium" size="small" />
            </div>
            <div className={`${classes.narrative} mp-mask`}><Text type="detail">{insight.narrative}</Text></div>
            {(insight.confidence === 'low' || insight.visibility === 'private') && (
                <div className={classes.meta}>
                    {insight.confidence === 'low' && <span className={classes.lowTag}>Low confidence</span>}
                    {insight.visibility === 'private' && (
                        <span className={classes.lock}><LockIcon color="medium" size="small" /> Only you</span>
                    )}
                </div>
            )}
            {/* The feedback row has its own controls; taps anywhere in it (including its label and
                whitespace) must not fall through to the card's open-drawer tap target. */}
            {showFeedback && (
                <div onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
                    <InsightFeedback insight={insight} model={model} surface="insights-page" />
                </div>
            )}
        </div>
    );
};
